import { HTTPError } from "@marketplace/http";
import {
  Button,
  ConditionalRendering,
  Loader,
  Modal,
  Text
} from "@marketplace/ui";
import { notifications } from "@marketplace/ui/notifications";
import clsx from "clsx";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdCancel,
  MdCheckCircle,
  MdErrorOutline,
  MdInfoOutline,
  MdSync
} from "react-icons/md";

import { usePostRetryFailedIntegrationStepMutation } from "../../hooks/mutations/use-post-retry-failed-integration-step-mutation";
import { useGetIntegrationStatusQuery } from "../../hooks/queries/use-get-integration-status-query";
import type { IntegrationStepStatus } from "../../types";

type IntegrationStatusModalProps = {
  opened: boolean;
  onClose: () => void;
  orderId: string | null;
};

type RetryError = {
  stepName: string;
  message: string;
};

const getApiErrorMessage = async (
  error: unknown,
  fallback: string
): Promise<string> => {
  if (error instanceof HTTPError) {
    try {
      const body = (await error.response.json()) as { message?: string };

      if (body.message) {
        return body.message;
      }
    } catch {
      // Ignore parsing errors and fall back to generic message
    }
  }

  return fallback;
};

const formatIntegrationResponse = (response?: string): string => {
  if (!response) {
    return "";
  }

  try {
    return JSON.stringify(JSON.parse(response), null, 2);
  } catch {
    return response;
  }
};

const StepStatusIcon = ({ status }: { status: IntegrationStepStatus }) => {
  switch (status) {
    case "completed":
      return <MdCheckCircle size={32} className="text-noble-blue-500" />;
    case "failed":
    case "error":
      return <MdCancel size={32} className="text-[#FF3B30]" />;
    default:
      return <MdSync size={32} className="text-noble-blue-500" />;
  }
};

export const IntegrationStatusModal: FC<IntegrationStatusModalProps> = ({
  opened,
  onClose,
  orderId
}) => {
  const { t } = useTranslation("order", {
    keyPrefix: "orderListPage.integrationStatusModal"
  });
  const { t: tCommon } = useTranslation("common");

  const { data, isLoading, isError } = useGetIntegrationStatusQuery({
    queryParams: { orderId: orderId ?? "" },
    options: {
      enabled: opened && !!orderId,
      retry: false
    }
  });

  const [retryError, setRetryError] = useState<RetryError | null>(null);
  const [retryingStepName, setRetryingStepName] = useState<string | null>(null);

  const { mutate } = usePostRetryFailedIntegrationStepMutation({
    options: {
      onSuccess: (response) => {
        setRetryingStepName(null);
        setRetryError(null);
        notifications.show({
          message: response.message
        });
      },
      onError: async (error, variables) => {
        const message = await getApiErrorMessage(
          error,
          tCommon("errors.somethingWentWrong")
        );

        setRetryingStepName(null);
        setRetryError({
          stepName: variables.stepName,
          message
        });
        notifications.show({
          variant: "error",
          message: tCommon("errors.somethingWentWrong")
        });
      }
    }
  });

  const steps = data?.data?.steps ?? [];
  const currentOrderId = data?.data?.order_id ?? orderId ?? "";

  useEffect(() => {
    if (!opened) {
      setRetryError(null);
      setRetryingStepName(null);
    }
  }, [opened]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text type="heading">{t("title")}</Text>}
      classNames={{
        header: "!p-6 !min-h-fit",
        body: "!p-6 !pt-0"
      }}
      size={540}
    >
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center py-8 gap-2 text-fjord-grey-400">
          <MdErrorOutline size={48} />
          <Text type="heading" size="xxs">
            {t("notFound")}
          </Text>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {steps.map((step, index) => {
            const isFailed = step.status === "failed";
            const showRetry = isFailed;

            return (
              <div
                key={step.step_name}
                className={clsx(
                  "flex flex-col border border-solid rounded-lg p-2 gap-3",
                  isFailed ? "border-[#FF3B30]" : "border-mischka-400"
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-2 text-fjord-grey-500">
                    <Text size="l">{t("step", { number: index + 1 })}</Text>
                    <Text type="heading" size="xs" weight="bold">
                      {step.step_label}
                    </Text>
                  </div>
                  <div
                    className={clsx(
                      "w-10 h-10 flex items-center justify-center rounded-lg bg-[#F1F9FF]",
                      {
                        "bg-[#FFE0DF]":
                          step.status === "failed" || step.status === "error"
                      }
                    )}
                  >
                    <StepStatusIcon status={step.status} />
                  </div>
                </div>
                {showRetry && (
                  <>
                    <ConditionalRendering
                      condition={showRetry && step.response?.length}
                    >
                      <div className="space-y-2">
                        <div className="flex items-center gap-1">
                          <MdInfoOutline size={14} className="text-[#FF3B30]" />
                          <Text size="xs" className="text-fjord-grey-400">
                            Failure detail
                          </Text>
                        </div>
                        <div className="flex rounded-l-lg bg-mischka-100 overflow-hidden items-stretch">
                          <div className="min-h-full w-1 bg-[#FF3B30]" />
                          <pre className="text-fjord-grey-400 text-sm whitespace-pre-wrap break-words font-mono !my-0 p-1 block flex-1">
                            {formatIntegrationResponse(step.response)}
                          </pre>
                        </div>
                      </div>
                    </ConditionalRendering>
                    <Button
                      variant="primary"
                      fullWidth
                      rightSection={<MdSync size={20} />}
                      loading={retryingStepName === step.step_name}
                      onClick={() => {
                        setRetryError(null);
                        setRetryingStepName(step.step_name);
                        mutate({
                          orderId: currentOrderId,
                          stepName: step.step_name,
                          stepIndex: index
                        });
                      }}
                    >
                      {t("retryFailedStep")}
                    </Button>
                    {retryError?.stepName === step.step_name && (
                      <Text
                        type="text"
                        size="s"
                        className="text-[#FF3B30] break-words"
                      >
                        {retryError.message}
                      </Text>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};
